import type { Role } from '@prisma/client';
import { isDirectorRole, isStaffRole } from '@/lib/roles';

/** Visi staff meniu raktai (sutampa su admin-nav.ts). */
const ALL_STAFF_NAV_KEYS = [
  'nav-dashboard',
  'nav-work-acts',
  'nav-commercial-offers',
  'nav-tasks',
  'nav-calendar',
  'nav-workers',
  'nav-attendance',
  'nav-admin-tasks',
  'nav-history',
  'nav-objects',
  'nav-warehouse',
  'nav-partners',
  'nav-pending-receipts',
  'nav-vehicles',
  'nav-other-docs',
] as const;

const NAV_KEY_HREF: Record<string, string> = {
  'nav-dashboard': '/admin-dashboard',
  'nav-work-acts': '/work-acts',
  'nav-commercial-offers': '/commercial-offers',
  'nav-tasks': '/task-management',
  'nav-calendar': '/calendar',
  'nav-workers': '/worker-management',
  'nav-attendance': '/attendance',
  'nav-admin-tasks': '/admin-tasks',
  'nav-history': '/history',
  'nav-objects': '/objects',
  'nav-warehouse': '/warehouse',
  'nav-partners': '/partners',
  'nav-pending-receipts': '/pending-receipts',
  'nav-vehicles': '/automobiliai',
  'nav-other-docs': '/other-documents',
};

/** Moduliai, kuriuos direktorius gali priskirti administratoriui. */
export const ASSIGNABLE_ADMIN_MODULE_KEYS = ALL_STAFF_NAV_KEYS.filter((key) => key !== 'nav-workers');

/** Visada tik direktoriui — ne administratoriaus meniu. */
export const DIRECTOR_ONLY_PATH_PREFIXES = [
  '/worker-management',
  '/settings',
  '/api/staff-users',
  '/api/settings',
] as const;

/** Admin visada gali kviesti (meniu, pranešimai) — ne modulio API. */
const ADMIN_API_ALWAYS_ALLOWED_PREFIXES = [
  '/api/user/nav-preferences',
  '/api/notifications',
  '/api/auth/',
] as const;

export function parseAllowedModuleKeys(raw: unknown): string[] | null {
  if (raw == null) return null;
  if (!Array.isArray(raw)) return null;
  const keys = raw.map(String).filter((k) => ASSIGNABLE_ADMIN_MODULE_KEYS.includes(k));
  return keys.length > 0 ? keys : [];
}

/** Efektyvus administratoriaus modulių sąrašas (null DB = visi priskiriami). */
export function effectiveAdminModuleKeys(stored: unknown): string[] {
  const parsed = parseAllowedModuleKeys(stored);
  if (parsed === null) return [...ASSIGNABLE_ADMIN_MODULE_KEYS];
  return parsed;
}

export function allowedNavKeysForUser(
  role: string | undefined | null,
  storedModuleKeys: unknown,
): string[] {
  if (!isStaffRole(role)) return [];
  if (isDirectorRole(role)) {
    return [...ALL_STAFF_NAV_KEYS];
  }
  const keys = effectiveAdminModuleKeys(storedModuleKeys);
  if (keys.includes('nav-warehouse') && !keys.includes('nav-pending-receipts')) {
    keys.push('nav-pending-receipts');
  }
  if (
    (keys.includes('nav-pending-receipts') || keys.includes('nav-warehouse')) &&
    !keys.includes('nav-partners')
  ) {
    keys.push('nav-partners');
  }
  return keys;
}

export function pathnameToModuleKey(pathname: string): string | null {
  if (pathname.startsWith('/admin-dashboard')) return 'nav-dashboard';
  if (pathname.startsWith('/work-acts')) return 'nav-work-acts';
  if (pathname.startsWith('/commercial-offers')) return 'nav-commercial-offers';
  if (pathname.startsWith('/task-management')) return 'nav-tasks';
  if (pathname.startsWith('/calendar')) return 'nav-calendar';
  if (pathname.startsWith('/worker-management')) return 'nav-workers';
  if (pathname.startsWith('/attendance') || pathname.startsWith('/overtime-requests')) return 'nav-attendance';
  if (pathname.startsWith('/admin-tasks')) return 'nav-admin-tasks';
  if (pathname.startsWith('/history')) return 'nav-history';
  if (pathname.startsWith('/objects') || pathname.startsWith('/equipment')) return 'nav-objects';
  if (pathname.startsWith('/pending-receipts')) return 'nav-pending-receipts';
  if (pathname.startsWith('/partners')) return 'nav-partners';
  if (pathname.startsWith('/warehouse')) return 'nav-warehouse';
  if (pathname.startsWith('/automobiliai')) return 'nav-vehicles';
  if (pathname.startsWith('/other-documents')) return 'nav-other-docs';
  if (pathname.startsWith('/reports')) return 'nav-other-docs';
  return null;
}

/** Puslapio ar API kelio → modulio raktas (admin teisių tikrinimui). */
export function requestPathToModuleKey(pathname: string): string | null {
  if (pathname.startsWith('/api/')) {
    if (ADMIN_API_ALWAYS_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p))) {
      return null;
    }
    if (pathname.startsWith('/api/dashboard')) return 'nav-dashboard';
    if (pathname.startsWith('/api/work-acts')) return 'nav-work-acts';
    if (pathname.startsWith('/api/commercial-offers')) return 'nav-commercial-offers';
    if (
      pathname.startsWith('/api/tasks') ||
      pathname.startsWith('/api/work-templates') ||
      pathname.startsWith('/api/time-entries')
    ) {
      return 'nav-tasks';
    }
    if (pathname.startsWith('/api/calendar')) return 'nav-calendar';
    if (pathname.startsWith('/api/overtime-requests')) return 'nav-attendance';
    if (pathname.startsWith('/api/admin-tasks')) return 'nav-admin-tasks';
    if (pathname.startsWith('/api/attendance')) return 'nav-attendance';
    if (pathname.startsWith('/api/history')) return 'nav-history';
    if (
      pathname.startsWith('/api/objects') ||
      pathname.startsWith('/api/warranty') ||
      pathname.startsWith('/api/equipment') ||
      pathname.startsWith('/api/clients') ||
      pathname.startsWith('/api/equipment-types') ||
      pathname.startsWith('/api/equipment-model-options') ||
      pathname.startsWith('/api/work-locations')
    ) {
      return 'nav-objects';
    }
    if (pathname.startsWith('/api/materials') || pathname.startsWith('/api/task-receipts')) {
      return 'nav-warehouse';
    }
    if (pathname.startsWith('/api/business-partners')) return 'nav-partners';
    if (pathname.startsWith('/api/vehicles')) return 'nav-vehicles';
    if (pathname.startsWith('/api/admin-documents') || pathname.startsWith('/api/reports')) {
      return 'nav-other-docs';
    }
    if (pathname.startsWith('/api/workers')) {
      if (pathname.startsWith('/api/workers/me/')) return null;
      return 'nav-tasks';
    }
    return null;
  }
  return pathnameToModuleKey(pathname);
}

export function isDirectorOnlyPath(pathname: string): boolean {
  return DIRECTOR_ONLY_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export function isAdminModulePathAllowed(
  pathname: string,
  role: Role | string | undefined | null,
  storedModuleKeys: unknown,
): boolean {
  if (!isStaffRole(role)) return false;
  if (isDirectorRole(role)) return true;
  if (isDirectorOnlyPath(pathname)) return false;

  if (pathname.startsWith('/api/')) {
    if (ADMIN_API_ALWAYS_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p))) {
      return true;
    }
  }

  const moduleKey = requestPathToModuleKey(pathname);
  if (!moduleKey) {
    if (pathname.startsWith('/api/')) {
      // Worker app — savo GPS, užduotys, profilis (paliekame Deivido srautą).
      if (pathname.startsWith('/api/workers/me/')) return true;
      return false;
    }
    return true;
  }

  const allowed = effectiveAdminModuleKeys(storedModuleKeys);
  return allowed.includes(moduleKey);
}

export function firstAllowedAdminPath(storedModuleKeys: unknown): string {
  const allowed = effectiveAdminModuleKeys(storedModuleKeys);
  for (const key of allowed) {
    const href = NAV_KEY_HREF[key];
    if (href) return href;
  }
  return '/login';
}

export function sanitizeAdminModuleKeysInput(keys: string[]): string[] {
  const set = new Set<string>();
  for (const key of keys) {
    if (ASSIGNABLE_ADMIN_MODULE_KEYS.includes(key)) set.add(key);
  }
  return ASSIGNABLE_ADMIN_MODULE_KEYS.filter((k) => set.has(k));
}
